import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const useInstallPrompt = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [browserType, setBrowserType] = useState('unknown');
  const [isStandalone, setIsStandalone] = useState(false);
  const [hasBeenDismissed, setHasBeenDismissed] = useState(false);

  // Detect browser type and PWA support
  const detectBrowser = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
    const isChrome = /chrome/.test(userAgent) && !/edg/.test(userAgent);
    const isEdge = /edg/.test(userAgent);
    const isFirefox = /firefox/.test(userAgent);
    const isSamsungInternet = /samsungbrowser/.test(userAgent);

    if (isIOS) {
      return 'ios';
    } else if (isSafari) {
      return 'safari';
    } else if (isChrome) {
      return 'chrome';
    } else if (isEdge) {
      return 'edge';
    } else if (isFirefox) {
      return 'firefox';
    } else if (isSamsungInternet) {
      return 'samsung';
    }
    return 'unknown';
  };

  // Check if already installed as PWA
  const checkIfInstalled = () => {
    // Multiple detection methods for PWA installation
    const standaloneMedia = window.matchMedia('(display-mode: standalone)').matches;
    const standaloneNavigator = window.navigator.standalone === true;
    const androidApp = document.referrer.includes('android-app://');

    // Check for PWA-specific window properties
    const windowFullScreen = window.matchMedia('(display-mode: fullscreen)').matches;
    const windowMinimal = window.matchMedia('(display-mode: minimal-ui)').matches;

    // Check localStorage for manual installation tracking
    const manuallyInstalled = localStorage.getItem('pwa-manually-installed') === 'true';

    // Check URL parameters that might indicate PWA launch
    const urlParams = new URLSearchParams(window.location.search);
    const pwaSource = urlParams.get('source') === 'pwa';

    // More comprehensive standalone detection
    const standalone = standaloneMedia ||
                      standaloneNavigator ||
                      androidApp ||
                      windowFullScreen ||
                      windowMinimal ||
                      manuallyInstalled ||
                      pwaSource;

    // Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('PWA Detection Debug:', {
        'matchMedia standalone': standaloneMedia,
        'navigator.standalone': standaloneNavigator,
        'android referrer': androidApp,
        'fullscreen mode': windowFullScreen,
        'minimal-ui mode': windowMinimal,
        'manually installed': manuallyInstalled,
        'pwa source param': pwaSource,
        'final standalone': standalone,
        'referrer': document.referrer,
        'location': window.location.href
      });
    }

    return standalone;
  };

  // Check if browser supports PWA installation
  const isBrowserSupported = (browser) => {
    const supportedBrowsers = ['chrome', 'edge', 'samsung', 'ios'];
    return supportedBrowsers.includes(browser);
  };

  // Check dismissal state
  const checkDismissalState = () => {
    const dismissedPermanent = localStorage.getItem('pwa-install-dismissed-permanent');
    if (dismissedPermanent === 'true') {
      return true;
    }

    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedDate = localStorage.getItem('pwa-install-dismissed-date');

    if (dismissed && dismissedDate) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedDate)) / (1000 * 60 * 60 * 24);
      // Show again after 7 days
      return daysSinceDismissed < 7;
    }
    return false;
  };

  useEffect(() => {
    const browser = detectBrowser();
    const standalone = checkIfInstalled();
    const dismissed = checkDismissalState();
    
    setBrowserType(browser);
    setIsStandalone(standalone);
    setHasBeenDismissed(dismissed);

    // Handle beforeinstallprompt event (Chrome, Edge, Samsung Internet)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Only show if browser is supported, not already installed, and not dismissed
      if (isBrowserSupported(browser) && !standalone && !dismissed) {
        setIsInstallable(true);
      }
    };

    // Handle iOS/Safari - they don't have beforeinstallprompt
    const handleiOSSafari = () => {
      if ((browser === 'ios' || browser === 'safari') && !standalone && !dismissed) {
        // Small delay to ensure page is loaded
        setTimeout(() => {
          setIsInstallable(true);
        }, 3000);
      }
    };

    // Handle app installed event
    const handleAppInstalled = () => {
      console.log('PWA was installed via browser install prompt');
      localStorage.setItem('pwa-manually-installed', 'true');
      localStorage.setItem('pwa-install-date', Date.now().toString());
      setIsStandalone(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check for iOS/Safari after component mount
    handleiOSSafari();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (deferredPrompt) {
      // Chrome, Edge, Samsung Internet
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        // Mark as manually installed to track state
        localStorage.setItem('pwa-manually-installed', 'true');
        localStorage.setItem('pwa-install-date', Date.now().toString());
      } else {
        console.log('User dismissed the install prompt');
        markAsDismissed();
      }

      setDeferredPrompt(null);
      setIsInstallable(false);
    }
  };

  const markAsDismissed = () => {
    localStorage.setItem('pwa-install-dismissed', 'true');
    localStorage.setItem('pwa-install-dismissed-date', Date.now().toString());
    setIsInstallable(false);
    setHasBeenDismissed(true);
  };

  const markAsDismissedPermanent = () => {
    localStorage.setItem('pwa-install-dismissed-permanent', 'true');
    setIsInstallable(false);
    setHasBeenDismissed(true);
  };

  const getInstallInstructions = () => {
    switch (browserType) {
      case 'ios':
        return {
          title: t('install_app_title'),
          steps: [
            t('ios_step_1'),
            t('ios_step_2'), 
            t('ios_step_3')
          ],
          icon: '📱'
        };
      case 'safari':
        return {
          title: t('install_app_title'),
          steps: [
            t('safari_step_1'),
            t('safari_step_2'),
            t('safari_step_3')
          ],
          icon: '🧭'
        };
      case 'chrome':
      case 'edge':
      case 'samsung':
        return {
          title: t('install_app_title'),
          steps: [
            t('chrome_step_1'),
            t('chrome_step_2')
          ],
          icon: '💻'
        };
      default:
        return null;
    }
  };

  // Reset install state (for debugging/testing)
  const resetInstallState = () => {
    localStorage.removeItem('pwa-manually-installed');
    localStorage.removeItem('pwa-install-date');
    localStorage.removeItem('pwa-install-dismissed');
    localStorage.removeItem('pwa-install-dismissed-date');
    localStorage.removeItem('pwa-install-dismissed-permanent');
    setIsStandalone(false);
    setHasBeenDismissed(false);
    console.log('PWA install state reset');
  };

  return {
    isInstallable: isInstallable && !isStandalone && isBrowserSupported(browserType),
    browserType,
    isStandalone,
    deferredPrompt,
    promptInstall,
    markAsDismissed,
    markAsDismissedPermanent,
    resetInstallState,
    getInstallInstructions: getInstallInstructions()
  };
};

export default useInstallPrompt; 